// Shared UI/formatting helpers for the Project Analysis surfaces (internal page,
// comparison, and the client share page). Pure presentation — no calculation.
import { useState, useEffect, useRef } from 'react';

// One consistent colour language across every chart + legend on every surface.
//   value = property value, invested = cash in, rent = net rent,
//   appreciation = market appreciation, equity = instant equity (discount),
//   roi = combined ROI line, payment = cumulative payments in.
export const CHART_COLORS = {
  value: '#1E3A5F',        // deep navy — property value line
  invested: '#B98A3D',     // muted gold — total invested / scheduled
  rent: '#3B6E58',         // muted forest — net rent / long-term series
  appreciation: '#1D9E75', // brand green — market appreciation (gains)
  equity: '#0EA894',       // teal — instant equity highlight
  roi: '#1E3A5F',          // deep navy — combined ROI line
  payment: '#1D9E75',      // brand green — cumulative payments in
  handover: '#B98A3D',     // gold — handover reference marker
};

// Compact axis notation: 1_200_000 -> "1.2M", 600_000 -> "600k", 950 -> "950".
export function compact(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1e6) return (v / 1e6).toFixed(abs >= 1e7 ? 0 : 1).replace(/\.0$/, '') + 'M';
  if (abs >= 1e3) return Math.round(v / 1e3) + 'k';
  return String(Math.round(v));
}

// Whole-number grouped string (no currency prefix). "1,234,567".
export function grouped(n) {
  return Math.round(Number(n) || 0).toLocaleString('en-GB');
}

// Animated count-up. Returns a number that eases from its previous value to the
// new target over `ms`, so ROI/stat cards feel responsive when assumptions change.
// Honours prefers-reduced-motion (jumps straight to the value).
export function useCountUp(target, ms = 550) {
  const t = Number(target) || 0;
  const [val, setVal] = useState(t);
  const fromRef = useRef(t);
  const rafRef = useRef(null);
  const startRef = useRef(0);

  useEffect(() => {
    const reduce = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const from = fromRef.current;
    if (reduce || from === t) { fromRef.current = t; setVal(t); return; }
    startRef.current = 0;
    const ease = (x) => 1 - Math.pow(1 - x, 3); // easeOutCubic
    const tick = (now) => {
      if (!startRef.current) startRef.current = now;
      const p = Math.min(1, (now - startRef.current) / ms);
      setVal(from + (t - from) * ease(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = t;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [t, ms]);

  return val;
}
